import MarkedReact from "marked-react";
import React, { memo } from "react";
import ReactDOM from "react-dom";
import {
  getChatDesignerRuntime,
  isDangerousChatHref,
  parseAskLinkRef,
  parseScreenLinkRef
} from "../../../../../shared/utility/kuika-chat-links";
import KuikaPopover from "../../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSmallSpinner from "../../../../ui-kit/kuika-small-spinner/kuika-small-spinner";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { expandMentionContext } from "../input/mcp-chat-mention-data";
// Type-only edge back to the panel, so this stays a leaf at runtime.
import { getMcpChatPanelInstance } from "../panel/mcp-chat-panel-ref";
import { highlightText } from "../search/mcp-chat-search";
import type {
  ChatOption,
  ConfirmationInterruptData,
  DiffActivityGroup,
  DiffTreeNode,
  McpChatMessage as McpChatMessageType,
  MentionReference,
  ParsedQuestionBlock,
  ParsedQuestionnaire,
  ParsedTaskPlan,
  QuestionnaireProgress,
  SelectedElementAttachment,
  StructuralDiff
} from "../shared/mcp-chat-types";
import {
  detectPlanMessage,
  detectSpecMessage,
  parseChangeDeltaId,
  stripSpecMachineAnchors,
  formatStepCount,
  formatStepLabel,
  isOtherLikeValue,
  isOtherSelection,
  mergePlanCompletion,
  parseAssistantMessage
} from "../shared/mcp-chat-utils";
import {
  areAllQuestionsAnswered,
  areAllQuestionsResolved,
  isQuestionAnswered,
  isQuestionDelegated
} from "../shared/questionnaire-answered";
import McpChatMessageFooter from "./mcp-chat-message-footer";
import { McpToolTranscript } from "./mcp-chat-tool-transcript-view";

interface McpChatMessageProps {
  message: McpChatMessageType;
  searchQuery?: string;
  isActiveSearchMatch?: boolean;
  staggerIndex?: number;
  isActiveTabLoading: boolean;
  isMostRecentUndoable: boolean;
  /**
   * Whether the rewind feature is available on the current project.
   * When false, no rewind button is rendered regardless of message state.
   */
  rewindEnabled: boolean;
  /**
   * For assistant messages: the user-message id to rewind to when the
   * persistent "Undo" pill at the bottom of this bubble is clicked.
   * Set on the latest non-error assistant message iff a rewindable user
   * message exists before it. Null everywhere else.
   */
  undoButtonTargetUserMsgId: string | null;
  questionnaireProgress?: QuestionnaireProgress | null;
  tabPlanMode: boolean;
  planJson?: string | null;
  /** True for the latest non-error assistant message — pins its footer visible. */
  isLastAssistant?: boolean;
  /** Submit a per-message rating (OK/Not OK + optional reason). */
  onRate?: (chatRequestId: string, rating: number, reason?: string) => void;
  onRetry: (messageId: string) => void;
  onUndo: (messageId: string) => void;
  onPlanApprove: (messageId: string) => void;
  onPlanReject: (messageId: string) => void;
  onSpecApprove: (messageId: string) => void;
  onSpecReject: (messageId: string) => void;
  /** KOA-18422 — a change plan's Approve/Revise. Carries the delta id, not the markdown. */
  onChangeApprove: (messageId: string, deltaId: string) => void;
  onChangeReject: (messageId: string, deltaId: string) => void;
  onInterruptApprove: (messageId: string) => void;
  onInterruptApproveAll: (messageId: string) => void;
  onInterruptReject: (messageId: string) => void;
  onOptionSelect: (value: string) => void;
  onQuestionOptionSelect: (
    messageId: string,
    questionIndex: number,
    value: string,
    questionCount: number,
    question?: ParsedQuestionBlock
  ) => void;
  onQuestionMultiToggle: (messageId: string, questionIndex: number, value: string) => void;
  onQuestionCustomValueChange: (messageId: string, questionIndex: number, customValue: string) => void;
  onQuestionGoTo: (messageId: string, targetIndex: number) => void;
  onQuestionBack: (messageId: string) => void;
  onQuestionNext: (messageId: string, questionCount: number, currentQuestion?: ParsedQuestionBlock) => void;
  onQuestionSubmit: (messageId: string, questionnaire: ParsedQuestionnaire) => void;
  onQuestionInterruptSubmit: (messageId: string, questionnaire: ParsedQuestionnaire) => void;
  /** "You decide this one" — hand back the current question and advance. */
  onQuestionDelegateOne: (messageId: string, questionIndex: number, questionCount: number) => void;
  /** "You decide the rest" — submit what is answered and end the questions. */
  onQuestionDelegateRest: (messageId: string, questionnaire: ParsedQuestionnaire) => void;
}

function toolQuestionsToQuestionnaire(parameters: Record<string, unknown>): ParsedQuestionnaire | null {
  const questions = parameters.questions;
  if (!Array.isArray(questions) || questions.length === 0) return null;

  return {
    intro: "",
    questions: questions.map((q: Record<string, unknown>) => ({
      prompt: String(q.prompt || ""),
      // `multiSelect` is first-class on the backend tool schema — carry it
      // through so the UI renders checkboxes + Submit button instead of
      // radio buttons. Falling back to the "(select all that apply)"
      // regex in mcp-chat-utils only applies to text-parsed
      // questionnaires, not tool-structured ones.
      multiSelect: q.multiSelect === true ? true : undefined,
      options: (Array.isArray(q.options) ? q.options : []).map((opt: Record<string, unknown>) => ({
        label: String(opt.label || ""),
        value: String(opt.label || ""),
        ...(opt.freeform === true ? { freeform: true } : {}),
        ...(opt.submitImmediately === true ? { submitImmediately: true } : {})
      }))
    })),
    outro: ""
  };
}

function renderAnsweredQuestionnaire(
  questionnaire: ParsedQuestionnaire,
  progress: QuestionnaireProgress
): React.ReactNode {
  const items = questionnaire.questions.map((q, i) => {
    const prompt =
      q.prompt
        .replace(/\r?\n+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/[:?]\s*$/, "")
        .trim() || `Question ${i + 1}`;

    // A question the user handed back is not an unanswered one. Rendering it as
    // a bare dash would read as a gap in the transcript when it was a choice.
    let answer = isQuestionDelegated(i, progress) ? "Decided by the analyst" : "—";
    if (q.multiSelect) {
      const multiValues = progress.multiSelectedValues?.[i] || [];
      if (multiValues.length > 0) {
        answer = multiValues.map((v) => q.options.find((o) => o.value === v)?.label || v).join(", ");
      }
    } else {
      const selected = progress.selectedValues[i];
      if (selected) {
        if (isOtherSelection(q, selected)) {
          const custom = (progress.customValues[i] || "").trim();
          if (custom) answer = custom;
        } else {
          answer = q.options.find((o) => o.value === selected)?.label || selected;
        }
      }
    }

    return { prompt, answer };
  });

  // A question can be handed back and then answered after a Back — the answer
  // wins in both the row above and this header, so a stale mark never claims
  // the analyst decided something the user actually chose.
  const anyDelegated: boolean = questionnaire.questions.some(
    (q: ParsedQuestionBlock, i: number) => isQuestionDelegated(i, progress) && !isQuestionAnswered(q, i, progress)
  );

  return (
    <div className="mcp-chat__answered-questions">
      <div className="mcp-chat__answered-questions-header">
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        {anyDelegated ? "Answered questions · rest decided by the analyst" : "Answered questions"}
      </div>
      <ul className="mcp-chat__answered-questions-list">
        {items.map((it, idx) => (
          <li key={idx} className="mcp-chat__answered-question-item">
            <div className="mcp-chat__answered-question-prompt">{it.prompt}</div>
            <div className="mcp-chat__answered-question-answer">{it.answer}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface WizardStepperProps {
  questions: ParsedQuestionBlock[];
  progress: QuestionnaireProgress;
  currentIndex: number;
  onGoTo: (stepIdx: number) => void;
  // KOA-17362 §24 — `restrictBackOnly` used to mean "backwards only", which left
  // the user stranded: after stepping back to revise an answer they could not
  // click forward again to the question they came from (only re-answering
  // advanced the wizard). Revising an answer must never cost you your place. It
  // now means "no JUMPING AHEAD to a question you have not answered yet": every
  // EARLIER step and every ANSWERED step stays reachable in both directions.
  restrictBackOnly?: boolean;
}

// Numbered step navigator for a multi-question wizard: accent nodes joined by
// a connector that fills as you progress. Answered steps show a checkmark, the
// current step is highlighted, upcoming steps are hollow. Centralises the
// "is this step answered" logic both wizard paths previously duplicated.
const WizardStepper: React.FC<WizardStepperProps> = ({
  questions,
  progress,
  currentIndex,
  onGoTo,
  restrictBackOnly
}) => {
  const isStepDone = (idx: number): boolean => {
    const q: ParsedQuestionBlock = questions[idx];
    if (q.multiSelect) {
      return (progress.multiSelectedValues?.[idx] || []).length > 0;
    }
    const sel: string | undefined = progress.selectedValues[idx];
    if (!sel) return false;
    if (isOtherSelection(q, sel)) {
      return (progress.customValues[idx] || "").trim().length > 0;
    }
    return true;
  };

  return (
    <div className="mcp-chat__stepper" role="list" aria-label={`Question ${currentIndex + 1} of ${questions.length}`}>
      {questions.map((_, idx) => {
        const isActive: boolean = idx === currentIndex;
        const isDone: boolean = !isActive && isStepDone(idx);
        // Answeredness wins: a step handed back and then answered on a Back is
        // answered, not skipped.
        const isSkipped: boolean = !isActive && !isDone && isQuestionDelegated(idx, progress);
        let state: string = "pending";
        if (isActive) state = "active";
        else if (isDone) state = "done";
        else if (isSkipped) state = "skipped";
        // A skipped step is settled, so it stays reachable in both directions for
        // the same reason an answered one does — the user may want to answer it.
        const canClick: boolean = restrictBackOnly
          ? idx !== currentIndex && (idx < currentIndex || isDone || isSkipped)
          : idx !== currentIndex;
        const lineFilled: boolean = idx < currentIndex;
        return (
          <React.Fragment key={idx}>
            <button
              type="button"
              className={`mcp-chat__stepper-node mcp-chat__stepper-node--${state}`}
              onClick={canClick ? () => onGoTo(idx) : undefined}
              disabled={!canClick}
              aria-current={isActive ? "step" : undefined}
              aria-label={`Question ${idx + 1}${isDone ? " (answered)" : ""}${isSkipped ? " (skipped)" : ""}`}
              title={`Question ${idx + 1}`}
            >
              {isDone && (
                <svg
                  viewBox="0 0 24 24"
                  width="12"
                  height="12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
              {/* Same box, stroke and cap as the checkmark so the two glyphs sit
                  identically in the 22px node — settled, just not by you. */}
              {isSkipped && (
                <svg
                  viewBox="0 0 24 24"
                  width="12"
                  height="12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <line x1="6" y1="12" x2="18" y2="12" />
                </svg>
              )}
              {!isDone && !isSkipped && <span className="mcp-chat__stepper-num">{idx + 1}</span>}
            </button>
            {idx < questions.length - 1 && (
              <span
                className={`mcp-chat__stepper-line${lineFilled ? " mcp-chat__stepper-line--filled" : ""}`}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Whether a confirmation interrupt would drop/delete a pre-existing artifact.
 * Drives the amber "Dangerous change" card. Derived (no wire-level severity
 * flag): any migration item flagged `isDrop`, or a drop/delete action verb.
 */
function isDestructiveConfirmation(data: ConfirmationInterruptData): boolean {
  if (data.detailItems && data.detailItems.some((item) => item.isDrop)) return true;
  const verb: string = (data.actionText || "").toLowerCase();
  return verb.includes("drop") || verb.includes("delete") || verb.includes("remove");
}

const SelectedElementBadge: React.FC<{
  attachment: SelectedElementAttachment;
  muted?: boolean;
}> = ({ attachment, muted }) => (
  <div
    className={`mcp-chat__selected-element-badge${muted ? " mcp-chat__selected-element-badge--muted" : ""}`}
    title={`${attachment.componentName} (${attachment.kuikaId})`}
  >
    <svg
      className="mcp-chat__selected-element-badge-icon"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <line x1="22" y1="12" x2="18" y2="12" />
      <line x1="6" y1="12" x2="2" y2="12" />
      <line x1="12" y1="6" x2="12" y2="2" />
      <line x1="12" y1="22" x2="12" y2="18" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </svg>
    <div className="mcp-chat__selected-element-badge-copy">
      <span className="mcp-chat__selected-element-badge-title">{attachment.displayName}</span>
    </div>
  </div>
);

const MENTION_CATEGORY_COLORS: Record<string, string> = {
  component: "#7c5cf5",
  datasource: "#0891b2",
  action: "#059669",
  screen: "#d97706",
  error: "#ef4444",
  language: "#2563eb"
};

const MessageMentionChip: React.FC<{ mention: MentionReference }> = ({ mention }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [tooltipContent, setTooltipContent] = React.useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = React.useState<{ top: number; left: number } | null>(null);
  const chipRef = React.useRef<HTMLSpanElement>(null);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      if (!tooltipContent) {
        setTooltipContent(expandMentionContext(mention));
      }
      if (chipRef.current) {
        const rect = chipRef.current.getBoundingClientRect();
        setTooltipPos({ top: rect.top - 6, left: rect.left });
      }
      setShowTooltip(true);
    }, 300);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setShowTooltip(false);
  };

  const color = MENTION_CATEGORY_COLORS[mention.category] || "#7c5cf5";

  return (
    <span
      ref={chipRef}
      className="mcp-chat__msg-mention"
      style={{ "--mention-color": color } as React.CSSProperties}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      @{mention.displayName}
      {showTooltip &&
        tooltipContent &&
        tooltipPos &&
        ReactDOM.createPortal(
          <span className="mcp-chat__msg-mention-tooltip" style={{ top: tooltipPos.top, left: tooltipPos.left }}>
            <span className="mcp-chat__msg-mention-tooltip-badge" style={{ color }}>
              {mention.category}
            </span>
            <pre className="mcp-chat__msg-mention-tooltip-body">{tooltipContent}</pre>
          </span>,
          document.body
        )}
    </span>
  );
};

const MENTION_PATTERN = /@\[([^\]]+)\]/g;

function renderUserContentWithMentions(
  content: string,
  mentions?: MentionReference[],
  searchQuery?: string,
  isActiveSearchMatch?: boolean
): React.ReactNode {
  const mentionMap = new Map<string, MentionReference>();
  if (mentions) {
    for (const m of mentions) {
      mentionMap.set(m.displayName, m);
    }
  }

  const wrapText = (text: string, key: string | number): React.ReactNode => {
    if (searchQuery) {
      return <HighlightedText key={key} text={text} query={searchQuery} isActive={isActiveSearchMatch} />;
    }
    return text;
  };

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const mentionRegex = new RegExp(MENTION_PATTERN.source, "g");

  while ((match = mentionRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(wrapText(content.slice(lastIndex, match.index), `text-${match.index}`));
    }

    const displayName = match[1];
    const ref = mentionMap.get(displayName);
    if (ref) {
      parts.push(<MessageMentionChip key={`mention-${ref.id}-${match.index}`} mention={ref} />);
    } else {
      parts.push(
        <span key={`mention-unknown-${match.index}`} className="mcp-chat__msg-mention">
          @{displayName}
        </span>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push(wrapText(content.slice(lastIndex), `text-end`));
  }

  return parts.length > 0 ? (
    parts
  ) : searchQuery ? (
    <HighlightedText text={content} query={searchQuery} isActive={isActiveSearchMatch} />
  ) : (
    content
  );
}

// ── Helpers: select component on canvas ──

const selectComponentOnCanvas = (kuikaId: number) => {
  try {
    const sd = (window as any).getSdInstance?.();
    const idStr = String(kuikaId);
    // Use the same method as the component tree panel
    sd?.displayItemProvider?.setSelectedDisplayItem?.(idStr);
  } catch {
    // ignore
  }
};

/**
 * Tries to find and select a component by matching a name from a description string.
 */
const selectComponentFromDescription = (description: string) => {
  try {
    const sd = (window as any).getSdInstance?.();
    const dict = sd?.screenProvider?.state?.screenDesignDictionary;
    if (!dict) return;

    const quotedMatch = description.match(/'([^']+)'/);
    const nameTokens: string[] = [];
    if (quotedMatch) nameTokens.push(quotedMatch[1]);

    const componentNamePattern = /\b([A-Z][a-zA-Z]*\d*)\b/g;
    let m: RegExpExecArray | null;
    while ((m = componentNamePattern.exec(description)) !== null) {
      if (
        ![
          "Adding",
          "Added",
          "Setting",
          "Set",
          "Configuring",
          "Configured",
          "Deleting",
          "Deleted",
          "Cloning",
          "Cloned",
          "Moving",
          "Moved",
          "Creating",
          "Created",
          "Saving",
          "Saved",
          "Updating",
          "Updated",
          "Removing",
          "Removed",
          "Executing",
          "Executed",
          "Renaming",
          "Renamed",
          "Applying",
          "Applied",
          "Reordering",
          "Reordered",
          "Body",
          "Header",
          "Footer"
        ].includes(m[1])
      ) {
        nameTokens.push(m[1]);
      }
    }

    for (const token of nameTokens) {
      for (const key in dict) {
        const di = dict[key];
        if (!di?.item) continue;
        if (di.friendlyName === token || di.item.name === token) {
          sd.displayItemProvider?.setSelectedDisplayItem?.(key);
          return;
        }
      }
    }
  } catch {
    // ignore
  }
};

// ── Changes Made card visuals ──

// Category → KuikaSvg asset name (all in /images/common). The category strings
// are produced by categorizeArtifactChange (mcp-chat-change-category.ts).
const DEFAULT_CATEGORY_ICON = "components";
const CATEGORY_ICONS: Record<string, string> = {
  "Added components": "components",
  "Updated components": "components",
  Actions: "bolt",
  "Data Binding": "link",
  Styles: "adjustments-horizontal",
  Screens: "app",
  "Screen Inputs": "keyboard",
  Validations: "checklist",
  Database: "table",
  Datasources: "database-search",
  Translations: "world-www",
  "Custom Code": "code",
  APIs: "api",
  "Scheduled Jobs": "calendar-time",
  Packages: "archive",
  "Offline Tables": "table",
  Authorization: "shield-check",
  Workflows: "automation",
  Folders: "folder-check",
  Images: "image",
  "Micro Apps": "apps",
  Project: "adjustments-horizontal"
};

type ChangeKind = "add" | "remove" | "update";

const CHANGE_KIND_ICON: Record<ChangeKind, string> = {
  add: "plus",
  remove: "trash",
  update: "edit"
};

const CHANGE_KIND_COLOR: Record<ChangeKind, string> = {
  add: "var(--mcp-success)",
  remove: "var(--mcp-error)",
  update: "var(--mcp-accent)"
};

// Infer the change kind from a past-tense description (output of toPastTense).
function deriveChangeKind(pastDescription: string): ChangeKind {
  if (/^(Added|Created|Installed|Activated)\b/.test(pastDescription)) return "add";
  if (/^(Deleted|Removed|Deactivated)\b/.test(pastDescription)) return "remove";
  return "update";
}

interface ChangeRowProps {
  kind: ChangeKind;
  label: string;
  meta?: string;
  onClick: () => void;
}

const ChangeRow: React.FC<ChangeRowProps> = ({ kind, label, meta, onClick }) => (
  <div
    className={`mcp-chat__diff-row mcp-chat__diff-row--${kind}`}
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") onClick();
    }}
  >
    <span className="mcp-chat__diff-row-glyph">
      <KuikaSvg name={CHANGE_KIND_ICON[kind]} type="common" width={11} height={11} color={CHANGE_KIND_COLOR[kind]} />
    </span>
    <span className="mcp-chat__diff-row-label">{label}</span>
    {meta ? <span className="mcp-chat__diff-row-meta">{meta}</span> : null}
  </div>
);

interface ChangeGroupProps {
  category: string;
  count: number;
  children: React.ReactNode;
}

const ChangeGroup: React.FC<ChangeGroupProps> = ({ category, count, children }) => {
  const [open, setOpen] = React.useState(false);
  const icon: string = CATEGORY_ICONS[category] ?? DEFAULT_CATEGORY_ICON;
  return (
    <div className={`mcp-chat__diff-group${open ? " mcp-chat__diff-group--open" : ""}`}>
      <button
        className="mcp-chat__diff-group-header"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        type="button"
      >
        <span className="mcp-chat__diff-group-icon">
          <KuikaSvg name={icon} type="common" width={13} height={13} color="var(--mcp-accent)" />
        </span>
        <span className="mcp-chat__diff-group-label">{category}</span>
        <span className="mcp-chat__diff-group-count">{count}</span>
        <span className="mcp-chat__diff-group-chevron">
          <KuikaSvg name="chevron-right" type="arrows" width={11} height={11} color="var(--dark-4)" />
        </span>
      </button>
      <div className={`mcp-chat__diff-group-collapse${open ? " mcp-chat__diff-group-collapse--open" : ""}`}>
        <div className="mcp-chat__diff-group-rows-wrap">
          <div className="mcp-chat__diff-group-rows">{children}</div>
        </div>
      </div>
    </div>
  );
};

const GERUND_TO_PAST: Array<[RegExp, string]> = [
  [/^Adding\b/, "Added"],
  [/^Creating\b/, "Created"],
  [/^Setting\b/, "Set"],
  [/^Deleting\b/, "Deleted"],
  [/^Moving\b/, "Moved"],
  [/^Cloning\b/, "Cloned"],
  [/^Renaming\b/, "Renamed"],
  [/^Configuring\b/, "Configured"],
  [/^Applying\b/, "Applied"],
  [/^Updating\b/, "Updated"],
  [/^Removing\b/, "Removed"],
  [/^Saving\b/, "Saved"],
  [/^Executing\b/, "Executed"],
  [/^Reordering\b/, "Reordered"],
  [/^Binding\b/, "Bound"],
  [/^Installing\b/, "Installed"],
  [/^Activating\b/, "Activated"],
  [/^Deactivating\b/, "Deactivated"],
  [/^Reading\b/, "Read"],
  [/^Inspecting\b/, "Inspected"],
  [/^Browsing\b/, "Browsed"],
  [/^Looking up\b/, "Looked up"],
  [/^Checking\b/, "Checked"]
];

function toPastTense(desc: string): string {
  for (const [pattern, replacement] of GERUND_TO_PAST) {
    if (pattern.test(desc)) return desc.replace(pattern, replacement);
  }
  return desc;
}

const StructuralDiffBlock: React.FC<{ diff: StructuralDiff }> = ({ diff }) => {
  const [expanded, setExpanded] = React.useState(false);

  const presentCategories: string[] = [];
  if (diff.tree.length > 0) presentCategories.push("Added components");
  for (const group of diff.activityGroups) presentCategories.push(group.category);
  const uniqueCategories: string[] = Array.from(new Set(presentCategories));

  return (
    <div className={`mcp-chat__diff${expanded ? " mcp-chat__diff--open" : ""}`}>
      <button className="mcp-chat__diff-toggle" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
        <span className="mcp-chat__diff-toggle-icon">
          <KuikaSvg name="sparkles" type="datasource-icons" width={13} height={13} color="var(--mcp-accent)" />
        </span>
        <span className="mcp-chat__diff-toggle-text">
          {diff.totalChanges} change{diff.totalChanges > 1 ? "s" : ""}
        </span>
        <span className="mcp-chat__diff-toggle-cats" aria-hidden="true">
          {uniqueCategories.slice(0, 6).map((category) => (
            <KuikaSvg
              key={category}
              name={CATEGORY_ICONS[category] ?? DEFAULT_CATEGORY_ICON}
              type="common"
              width={12}
              height={12}
              color="var(--dark-5)"
            />
          ))}
        </span>
        <span className="mcp-chat__diff-chevron">
          <KuikaSvg name="chevron-right" type="arrows" width={12} height={12} color="var(--dark-4)" />
        </span>
      </button>
      <div className={`mcp-chat__diff-collapse${expanded ? " mcp-chat__diff-collapse--open" : ""}`}>
        <div className="mcp-chat__diff-body">
          <div className="mcp-chat__diff-body-inner">
            {diff.tree.length > 0 && (
              <ChangeGroup category="Added components" count={diff.totalAdded}>
                {diff.tree.map((node: DiffTreeNode) => (
                  <ChangeRow
                    key={node.kuikaId}
                    kind="add"
                    label={node.displayName}
                    meta={node.componentType}
                    onClick={() => selectComponentOnCanvas(node.kuikaId)}
                  />
                ))}
              </ChangeGroup>
            )}
            {diff.activityGroups.map((group: DiffActivityGroup) => (
              <ChangeGroup key={group.category} category={group.category} count={group.items.length}>
                {group.items.map((desc: string, i: number) => {
                  const past: string = toPastTense(desc);
                  return (
                    <ChangeRow
                      key={i}
                      kind={deriveChangeKind(past)}
                      label={past}
                      onClick={() => selectComponentFromDescription(desc)}
                    />
                  );
                })}
              </ChangeGroup>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const FILE_TYPE_ICONS: Record<string, { color: string; label: string }> = {
  xlsx: { color: "#217346", label: "Excel" },
  xls: { color: "#217346", label: "Excel" },
  csv: { color: "#217346", label: "CSV" },
  pdf: { color: "#E53935", label: "PDF" },
  default: { color: "#5f6368", label: "File" }
};

function getFileInfo(fileName: string) {
  const ext = fileName.split(".").pop()?.toLowerCase() || "";
  return FILE_TYPE_ICONS[ext] || FILE_TYPE_ICONS.default;
}

function extractTextFromReactNode(node: React.ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractTextFromReactNode).join("");
  if (React.isValidElement(node) && node.props && (node.props as any).children) {
    return extractTextFromReactNode((node.props as any).children);
  }
  return "";
}

function formatFileNameForDisplay(fileName: string): string {
  if (fileName.length <= 35) return fileName;
  const ext = fileName.lastIndexOf(".");
  if (ext === -1) return fileName.slice(0, 32) + "...";
  const name = fileName.slice(0, ext);
  const extension = fileName.slice(ext);
  const maxName = 35 - extension.length - 3;
  return name.slice(0, Math.max(maxName, 8)) + "..." + extension;
}

const FileDownloadCard: React.FC<{ href: string; fileName: string }> = ({ href, fileName }) => {
  const fileInfo = getFileInfo(fileName);
  return (
    <a
      href={href}
      download={fileName}
      className="mcp-chat__file-card"
      onClick={(e) => {
        e.preventDefault();
        const link = document.createElement("a");
        link.href = href;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }}
    >
      <div className="mcp-chat__file-card-icon" style={{ backgroundColor: fileInfo.color + "18" }}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke={fileInfo.color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      </div>
      <div className="mcp-chat__file-card-info">
        <span className="mcp-chat__file-card-name">{formatFileNameForDisplay(fileName)}</span>
        <span className="mcp-chat__file-card-type">{fileInfo.label} — Click to download</span>
      </div>
      <svg
        className="mcp-chat__file-card-download"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    </a>
  );
};

/**
 * A page name in an assistant message that opens that page on the canvas.
 *
 * The "Couldn't do" section names the page each unfinished item belongs to; this makes
 * the name the affordance, the same way the validation-errors list in the footer has
 * always worked. Resolution happens at CLICK time against the live screen list, so a
 * message read days later still lands correctly (or says the screen is gone).
 */
const ScreenLink: React.FC<{ screenRef: string; text: React.ReactNode }> = ({ screenRef, text }) => {
  const [failed, setFailed] = React.useState<boolean>(false);
  const onClick = async (e: React.MouseEvent<HTMLAnchorElement>): Promise<void> => {
    e.preventDefault();
    const sd = getChatDesignerRuntime();
    if (!sd) {
      setFailed(true);
      return;
    }
    const { navigateToScreenRef } = await import("../../../../../shared/utility/kuika-chat-links");
    const result = await navigateToScreenRef(sd, screenRef);
    setFailed(!result.ok);
  };
  // Degrade to plain text rather than leaving a link that goes nowhere.
  if (failed) return <span className="mcp-chat__screen-link mcp-chat__screen-link--dead">{text}</span>;
  return (
    <a
      href="#"
      className="mcp-chat__screen-link"
      data-testid="mcpChatScreenLink"
      title={`Open ${screenRef} in the designer`}
      onClick={onClick}
    >
      {text}
    </a>
  );
};

/**
 * "How do I do this?" beside a "Couldn't do" item.
 *
 * The server ends each of those lines with the question already composed and localized, so this
 * only has to switch the tab to Ask and send it. That is the paste the section was designed for:
 * one line carries the page, the component, the action and the cause, which is exactly what the
 * follow-up needs to be answerable.
 *
 * Renders a button, not an anchor — it performs an action rather than navigating. The panel is
 * reached through the singleton because this message is rendered from both the floating panel and
 * the docked wrapper.
 */
const AskFollowUpLink: React.FC<{ question: string; text: React.ReactNode }> = ({ question, text }) => {
  const [sent, setSent] = React.useState<boolean>(false);
  const onClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    const panel = getMcpChatPanelInstance();
    if (!panel) return;
    // Only latch once the send is actually accepted. It is refused while a turn is in flight,
    // and disabling the button then would dead-end the item until the message is re-rendered.
    const sending: boolean = panel.askFollowUp(question);
    if (sending) setSent(true);
  };
  return (
    <button
      type="button"
      className="mcp-chat__ask-link"
      data-testid="mcpChatAskLink"
      title={question}
      disabled={sent}
      onClick={onClick}
    >
      {text}
    </button>
  );
};

const chatMarkdownRenderer = {
  link(href: string, text: React.ReactNode) {
    if (href && href.startsWith("blob:")) {
      const fileName = extractTextFromReactNode(text) || "download";
      return <FileDownloadCard key={href} href={href} fileName={fileName} />;
    }
    const screenRef: string | null = parseScreenLinkRef(href);
    if (screenRef !== null) return <ScreenLink key={href} screenRef={screenRef} text={text} />;
    const question: string | null = parseAskLinkRef(href);
    if (question !== null) return <AskFollowUpLink key={href} question={question} text={text} />;
    // Every href used to be rendered as a live anchor, `javascript:` included, in a
    // panel whose content comes from a model. Neutralize those to plain text.
    if (isDangerousChatHref(href)) return <span key={href}>{text}</span>;
    return (
      <a key={href} href={href} target="_blank" rel="noopener noreferrer">
        {text}
      </a>
    );
  }
};

const HighlightedText: React.FC<{ text: string; query: string; isActive?: boolean }> = ({ text, query, isActive }) => {
  const segments = highlightText(text, query);
  return (
    <>
      {segments.map((seg, i) =>
        seg.isMatch ? (
          <mark
            key={i}
            className={`mcp-chat__search-highlight${isActive ? " mcp-chat__search-highlight--active" : ""}`}
          >
            {seg.text}
          </mark>
        ) : (
          <React.Fragment key={i}>{seg.text}</React.Fragment>
        )
      )}
    </>
  );
};

function buildSearchAwareRenderer(query: string, isActive: boolean) {
  return {
    ...chatMarkdownRenderer,
    text(text: string) {
      return <HighlightedText text={text} query={query} isActive={isActive} />;
    }
  };
}

const InterruptActions: React.FC<{
  messageId: string;
  isActiveTabLoading: boolean;
  onApprove: (id: string) => void;
  onApproveAll: (id: string) => void;
  onReject: (id: string) => void;
}> = ({ messageId, isActiveTabLoading, onApprove, onApproveAll, onReject }) => {
  const [popoverOpen, setPopoverOpen] = React.useState(false);

  return (
    <div className="mcp-chat__plan-actions">
      <button
        className="mcp-chat__plan-btn mcp-chat__plan-btn--approve"
        data-testid="mcpChatApproveButton"
        onClick={() => onApprove(messageId)}
        disabled={isActiveTabLoading}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        Approve
      </button>
      <KuikaPopover
        visible={popoverOpen}
        onVisibleChange={setPopoverOpen}
        position="top"
        hideCloseIcon
        className="mcp-chat__approve-all-popover"
        content={
          <div className="mcp-chat__approve-all-confirm">
            <div className="mcp-chat__approve-all-confirm-icon">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <p className="mcp-chat__approve-all-confirm-text">
              Auto-approve <strong>all destructive operations</strong> for this session?
            </p>
            <p className="mcp-chat__approve-all-confirm-hint">
              Deletions and migrations will execute without asking. Revert is still available.
            </p>
            <div className="mcp-chat__approve-all-confirm-actions">
              <button
                className="mcp-chat__approve-all-confirm-btn mcp-chat__approve-all-confirm-btn--cancel"
                onClick={() => setPopoverOpen(false)}
              >
                Cancel
              </button>
              <button
                className="mcp-chat__approve-all-confirm-btn mcp-chat__approve-all-confirm-btn--enable"
                onClick={() => {
                  setPopoverOpen(false);
                  onApproveAll(messageId);
                }}
              >
                Enable
              </button>
            </div>
          </div>
        }
      >
        <button className="mcp-chat__plan-btn mcp-chat__plan-btn--approve-all" disabled={isActiveTabLoading}>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
            <polyline points="20 12 9 23 4 18" />
          </svg>
          Always Allow
        </button>
      </KuikaPopover>
      <button
        className="mcp-chat__plan-btn mcp-chat__plan-btn--reject"
        onClick={() => onReject(messageId)}
        disabled={isActiveTabLoading}
      >
        Cancel
      </button>
    </div>
  );
};

const AGENT_LABELS: Record<string, { label: string; icon: string }> = {
  UiStructureAgent: { label: "Structure", icon: "🏗️" },
  UiStylingAgent: { label: "Styling", icon: "🎨" },
  UiLogicAgent: { label: "Logic", icon: "⚡" },
  DatasourceAgent: { label: "Data Source", icon: "🔌" },
  DatabaseAgent: { label: "Database", icon: "🗄️" },
  CSharpDatasourceAgent: { label: "C# Source", icon: "⚙️" },
  UiProjectManagementAgent: { label: "Project", icon: "📁" },
  TranslationAgent: { label: "Translation", icon: "🌐" },
  MiscellaneousAgent: { label: "Processing", icon: "🔧" },
  RagAgent: { label: "Knowledge", icon: "📚" }
};

const TaskPlanBlock: React.FC<{ plan: ParsedTaskPlan }> = ({ plan }) => {
  const [expanded, setExpanded] = React.useState(true);
  const completedCount = plan.steps.filter((s) => s.completed).length;

  return (
    <div className="mcp-chat__task-plan">
      <button className="mcp-chat__task-plan-toggle" onClick={() => setExpanded((v) => !v)}>
        <svg className="mcp-chat__task-plan-chevron" width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path
            d={expanded ? "M2.5 6.5L5 4L7.5 6.5" : "M4 2.5L6.5 5L4 7.5"}
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>Execution Plan</span>
        <span className="mcp-chat__task-plan-count">{formatStepCount(completedCount, plan.steps.length)}</span>
      </button>
      {expanded && (
        <div className="mcp-chat__task-plan-steps">
          {plan.steps.map((step) => {
            const agentInfo = step.agent ? AGENT_LABELS[step.agent] : null;
            return (
              <div
                key={step.index}
                className={`mcp-chat__task-plan-step${step.completed ? " mcp-chat__task-plan-step--completed" : ""}`}
              >
                {(plan.steps.length > 1 || step.completed) && (
                  <span className="mcp-chat__task-plan-check">
                    {formatStepLabel(step.index, plan.steps.length, step.completed)}
                  </span>
                )}
                <span className="mcp-chat__task-plan-desc">{step.description}</span>
                {agentInfo && <span className="mcp-chat__task-plan-agent">{agentInfo.label}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface SimpleOptionsBlockParsed {
  textBefore: string;
  options: ChatOption[] | null;
  textAfter: string;
  multiSelect?: boolean;
}

interface SimpleOptionsBlockProps {
  parsed: SimpleOptionsBlockParsed;
  isActiveTabLoading: boolean;
  onOptionSelect: (value: string) => void;
}

const SimpleOptionsBlock: React.FC<SimpleOptionsBlockProps> = ({ parsed, isActiveTabLoading, onOptionSelect }) => {
  const [showCustomInput, setShowCustomInput] = React.useState(false);
  const [customValue, setCustomValue] = React.useState("");
  const [multiSelected, setMultiSelected] = React.useState<Set<string>>(new Set());
  const inputRef = React.useRef<HTMLInputElement>(null);
  const isMulti = !!parsed.multiSelect;

  React.useEffect(() => {
    if (showCustomInput) inputRef.current?.focus();
  }, [showCustomInput]);

  const handleSubmitCustom = () => {
    const trimmed = customValue.trim();
    if (!trimmed) return;
    if (isMulti) {
      setMultiSelected((prev) => {
        const next = new Set(prev);
        next.add(trimmed);
        return next;
      });
      setShowCustomInput(false);
      setCustomValue("");
    } else {
      onOptionSelect(trimmed);
      setShowCustomInput(false);
      setCustomValue("");
    }
  };

  const handleMultiToggle = (value: string) => {
    setMultiSelected((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const handleMultiSubmit = () => {
    if (multiSelected.size === 0) return;
    onOptionSelect(Array.from(multiSelected).join(", "));
  };

  return (
    <>
      {parsed.textBefore && <MarkedReact value={parsed.textBefore} renderer={chatMarkdownRenderer} />}
      <div
        data-testid="mcpChatQuestionnaire"
        className={`mcp-chat__options${isMulti ? " mcp-chat__options--multi" : ""}`}
      >
        {parsed.options!.map((opt, i) => {
          // KOA-16873: respect the parser-set `freeform: true` flag for
          // language-agnostic detection ("Diğer", "Andere", "Otro", …) and
          // fall back to the legacy English regex for backward compat.
          // Equivalent to `isOtherSelection(question, opt.value)` but works
          // with the option directly (no question wrapper at this level).
          const isOther = opt.freeform === true || isOtherLikeValue(opt.value);
          const isSelected = isMulti ? multiSelected.has(opt.value) : showCustomInput && isOther;
          return (
            <button
              key={i}
              className={`mcp-chat__option-chip ${isSelected ? "mcp-chat__option-chip--selected" : ""}`}
              disabled={isActiveTabLoading}
              onClick={() => {
                if (isOther) {
                  setShowCustomInput(true);
                } else if (isMulti) {
                  handleMultiToggle(opt.value);
                } else {
                  setShowCustomInput(false);
                  setCustomValue("");
                  onOptionSelect(opt.value);
                }
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {showCustomInput && (
        <div className="mcp-chat__custom-input-row">
          <input
            ref={inputRef}
            type="text"
            className="mcp-chat__custom-input"
            placeholder="Type your answer..."
            value={customValue}
            disabled={isActiveTabLoading}
            onChange={(e) => setCustomValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmitCustom();
              }
            }}
          />
          <button
            className="mcp-chat__custom-send-btn"
            disabled={isActiveTabLoading || !customValue.trim()}
            onClick={handleSubmitCustom}
          >
            {isMulti ? "Add" : "Send"}
          </button>
        </div>
      )}
      {isMulti && (
        <div className="mcp-chat__multi-submit-row">
          <span className="mcp-chat__multi-count">{multiSelected.size} selected</span>
          <button
            className="mcp-chat__custom-send-btn"
            disabled={isActiveTabLoading || multiSelected.size === 0}
            onClick={handleMultiSubmit}
          >
            Submit selections
          </button>
        </div>
      )}
      {parsed.textAfter && <MarkedReact value={parsed.textAfter} renderer={chatMarkdownRenderer} />}
    </>
  );
};

const McpChatMessageComponent: React.FC<McpChatMessageProps> = ({
  message: msg,
  staggerIndex,
  isActiveTabLoading,
  isMostRecentUndoable,
  rewindEnabled,
  undoButtonTargetUserMsgId,
  questionnaireProgress,
  tabPlanMode,
  planJson,
  isLastAssistant,
  onRate,
  searchQuery,
  isActiveSearchMatch,
  onRetry,
  onUndo,
  onPlanApprove,
  onPlanReject,
  onSpecApprove,
  onSpecReject,
  onChangeApprove,
  onChangeReject,
  onInterruptApprove,
  onInterruptApproveAll,
  onInterruptReject,
  onOptionSelect,
  onQuestionOptionSelect,
  onQuestionMultiToggle,
  onQuestionCustomValueChange,
  onQuestionGoTo,
  onQuestionBack,
  onQuestionNext,
  onQuestionSubmit,
  onQuestionInterruptSubmit,
  onQuestionDelegateOne,
  onQuestionDelegateRest
}) => {
  if (msg.role === "tool_status") {
    return (
      <div className="mcp-chat__message mcp-chat__message--tool">
        <div className="mcp-chat__tool-badge">
          {msg.toolStatus === "running" ? <KuikaSmallSpinner /> : null}
          <span>{msg.toolName || "Tool"}</span>
          {msg.toolStatus === "completed" && msg.content && (
            <span className="mcp-chat__tool-badge-detail">{msg.content}</span>
          )}
        </div>
      </div>
    );
  }

  const isUser = msg.role === "user";
  // A confirmation interrupt that will drop/delete a pre-existing artifact is
  // DESTRUCTIVE — it renders as an amber warning card, not a routine bubble.
  // No `severity` flag exists on the wire (ConfirmationInterruptData), so derive
  // it from the migration items (`isDrop`) or a drop/delete action verb.
  const isDestructiveInterrupt: boolean =
    !isUser &&
    !msg.isError &&
    msg.interruptData?.type === "confirmation" &&
    msg.interruptData.status === "pending" &&
    isDestructiveConfirmation(msg.interruptData as ConfirmationInterruptData);
  let bubbleClass: string;
  // Stable per-role hook for tests. The message wrapper's `mcpChatAssistantMessage`
  // testid covers every non-user message (errors included), so a bubble-level
  // testid is what lets tests target genuine assistant replies and exclude error
  // bubbles. Destructive-interrupt bubbles are still assistant replies, so they
  // share `mcpChatAssistantBubble`.
  let bubbleTestId: string;
  if (isUser) {
    bubbleClass = "mcp-chat__bubble mcp-chat__bubble--user";
    bubbleTestId = "mcpChatUserBubble";
  } else if (msg.isError) {
    bubbleClass = "mcp-chat__bubble mcp-chat__bubble--error";
    bubbleTestId = "mcpChatErrorBubble";
  } else if (isDestructiveInterrupt) {
    bubbleClass = "mcp-chat__bubble mcp-chat__bubble--assistant mcp-chat__bubble--danger";
    bubbleTestId = "mcpChatAssistantBubble";
  } else {
    bubbleClass = "mcp-chat__bubble mcp-chat__bubble--assistant";
    bubbleTestId = "mcpChatAssistantBubble";
  }

  const parsed = !isUser ? parseAssistantMessage(msg.content) : null;
  const taskPlan = parsed?.taskPlan || null;
  const hasTaskPlan = taskPlan != null;
  const questionnaire = parsed?.questionnaire || null;
  const hasQuestionnaire = questionnaire != null;
  const hasOptions = parsed?.options != null;
  // A questionnaire wizard shows one question at a time; letting the bubble
  // shrink-to-fit each question's content makes the panel jump horizontally
  // as the user steps through. Pin the hosting bubble to full width instead.
  const rendersQuestionnaire: boolean = hasQuestionnaire || msg.interruptData?.type === "question";
  if (rendersQuestionnaire && !isUser && !msg.isError) {
    bubbleClass += " mcp-chat__bubble--wide";
  }
  // Specialist agents (Wirer/Binder/Designer/Backend-Architect/Render-Fixer)
  // now render as entries inside the live tool transcript via the
  // `specialist_activity` event → `tab.toolActivities` pipeline. No more
  // message-level specialist bubble. The `streamingAgent` field stays on
  // `McpChatMessage` for search/history indexing but is no longer used
  // for rendering — legacy messages from pre-change sessions render as
  // ordinary text bubbles so their raw JSON content is still readable.
  const shouldRenderBubbleRow = isUser || msg.isError || msg.content.trim().length > 0;
  const questionCount = questionnaire?.questions.length || 0;
  const normalizedProgress: QuestionnaireProgress = questionnaireProgress || {
    currentQuestionIndex: 0,
    selectedValues: {},
    customValues: {}
  };
  const currentQuestionIndex =
    questionCount > 0 ? Math.max(0, Math.min(normalizedProgress.currentQuestionIndex, questionCount - 1)) : 0;
  const currentQuestion = questionCount > 0 ? questionnaire!.questions[currentQuestionIndex] : null;
  const isCurrentMultiSelect = !!currentQuestion?.multiSelect;
  const selectedValue = normalizedProgress.selectedValues[currentQuestionIndex] || "";
  const multiValues = normalizedProgress.multiSelectedValues?.[currentQuestionIndex] || [];
  const customValue = normalizedProgress.customValues[currentQuestionIndex] || "";
  const isCustomSelected = isOtherSelection(currentQuestion ?? undefined, selectedValue);
  const isCurrentAnswered = currentQuestion
    ? isQuestionAnswered(currentQuestion, currentQuestionIndex, normalizedProgress)
    : false;
  const isLastQuestion = currentQuestionIndex === questionCount - 1;
  const allQuestionsAnswered = questionCount > 0 && areAllQuestionsAnswered(questionnaire!, normalizedProgress);

  const renderBubbleContent = (): React.ReactNode => {
    if (isUser) {
      return renderUserContentWithMentions(msg.content, msg.mentions, searchQuery, isActiveSearchMatch);
    }
    if (msg.interruptData) {
      if (msg.interruptData.type === "question") {
        if (msg.interruptData.status !== "pending") {
          const answeredQuestionnaire = toolQuestionsToQuestionnaire(msg.interruptData.parameters);
          if (answeredQuestionnaire) {
            return renderAnsweredQuestionnaire(answeredQuestionnaire, normalizedProgress);
          }
          return <p className="mcp-chat__interrupt-desc">Answered questions</p>;
        }
        const questionQuestionnaire = toolQuestionsToQuestionnaire(msg.interruptData.parameters);
        if (questionQuestionnaire && questionQuestionnaire.questions.length > 0) {
          const qCount = questionQuestionnaire.questions.length;
          const qCurrentIndex =
            qCount > 0 ? Math.max(0, Math.min(normalizedProgress.currentQuestionIndex, qCount - 1)) : 0;
          const qCurrentQuestion = qCount > 0 ? questionQuestionnaire.questions[qCurrentIndex] : null;
          const qIsCurrentMultiSelect = !!qCurrentQuestion?.multiSelect;
          const qSelectedValue = normalizedProgress.selectedValues[qCurrentIndex] || "";
          const qMultiValues = normalizedProgress.multiSelectedValues?.[qCurrentIndex] || [];
          const qCustomValue = normalizedProgress.customValues[qCurrentIndex] || "";
          const qIsCustomSelected = isOtherSelection(qCurrentQuestion ?? undefined, qSelectedValue);
          const qIsCurrentSkipped = isQuestionDelegated(qCurrentIndex, normalizedProgress);
          const qIsCurrentAnswered = qCurrentQuestion
            ? isQuestionAnswered(qCurrentQuestion, qCurrentIndex, normalizedProgress)
            : false;
          const qIsLastQuestion = qCurrentIndex === qCount - 1;
          // The shared `areAllQuestionsAnswered` helper already handles the
          // multiSelect case (answers in multiSelectedValues) — it subsumes
          // master's inline KOA-17628 fix, so keep the single source of truth.
          // Submit accepts a question the user explicitly handed back, never one
          // they simply left untouched — the delegate buttons are the only way to
          // produce a gap, so nothing becomes submittable by accident.
          const qAllowDelegate = msg.interruptData.allowDelegate === true;
          const qAreAllResolved =
            qCount > 0 &&
            (qAllowDelegate
              ? areAllQuestionsResolved(questionQuestionnaire, normalizedProgress)
              : areAllQuestionsAnswered(questionQuestionnaire, normalizedProgress));

          if (qCurrentQuestion) {
            return (
              <>
                {qCount > 1 && (
                  <WizardStepper
                    questions={questionQuestionnaire.questions}
                    progress={normalizedProgress}
                    currentIndex={qCurrentIndex}
                    onGoTo={(stepIdx: number) => onQuestionGoTo(msg.id, stepIdx)}
                  />
                )}
                <p className="mcp-chat__question-prompt">{qCurrentQuestion.prompt}</p>
                {/* Skipping has to SAY something, or the click reads as a dead button —
                    on the last question there is no next step to advance to, so the
                    wizard's movement cannot stand in for feedback. The chips stay live
                    below, which is what makes undo just "pick one" rather than a
                    second button. */}
                {qIsCurrentSkipped && (
                  <p className="mcp-chat__question-skipped-note">
                    The analyst will decide this one. Pick an option to answer it yourself.
                  </p>
                )}
                {/* The option list is the whole card's "a question is pending" signal, and the
                    per-option index is the only language-independent way to address a choice —
                    an option carries a localized label and no id of its own. */}
                <div
                  data-testid="mcpChatQuestionnaire"
                  className={`mcp-chat__options ${
                    qIsCurrentMultiSelect ? "mcp-chat__options--multi" : "mcp-chat__options--single"
                  }`}
                >
                  {qCurrentQuestion.options.map((opt, optIndex) => {
                    const isSelected = qIsCurrentMultiSelect
                      ? qMultiValues.includes(opt.value)
                      : qSelectedValue === opt.value;
                    return (
                      <button
                        key={opt.value}
                        data-testid={`mcpChatQuestionOption-${optIndex}`}
                        className={`mcp-chat__option-chip${isSelected ? " mcp-chat__option-chip--selected" : ""}`}
                        onClick={() =>
                          qIsCurrentMultiSelect
                            ? onQuestionMultiToggle(msg.id, qCurrentIndex, opt.value)
                            : onQuestionOptionSelect(
                                msg.id,
                                qCurrentIndex,
                                opt.value,
                                qCount,
                                qCurrentQuestion ?? undefined
                              )
                        }
                        disabled={isActiveTabLoading}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {qIsCustomSelected && (
                  <input
                    className="mcp-chat__custom-input"
                    type="text"
                    placeholder="Type your answer..."
                    value={qCustomValue}
                    onChange={(e) => onQuestionCustomValueChange(msg.id, qCurrentIndex, e.target.value)}
                  />
                )}
                <div className="mcp-chat__wizard-nav">
                  {qCurrentIndex > 0 && (
                    <button
                      className="mcp-chat__wizard-btn mcp-chat__wizard-btn--secondary"
                      onClick={() => onQuestionBack(msg.id)}
                    >
                      Back
                    </button>
                  )}
                  {!qIsLastQuestion ? (
                    <button
                      className="mcp-chat__wizard-btn mcp-chat__wizard-btn--primary"
                      disabled={!qIsCurrentAnswered && !qIsCurrentSkipped}
                      onClick={() => onQuestionNext(msg.id, qCount, qCurrentQuestion ?? undefined)}
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      data-testid="mcpChatQuestionSubmit"
                      className="mcp-chat__wizard-btn mcp-chat__wizard-btn--primary mcp-chat__wizard-btn--submit"
                      disabled={isActiveTabLoading || !qAreAllResolved}
                      onClick={() => onQuestionInterruptSubmit(msg.id, questionQuestionnaire)}
                    >
                      Submit answers
                    </button>
                  )}
                </div>
                {/* Present on EVERY step, and never gated on answeredness — the
                    whole point is a way out for someone who does not want to
                    answer. "This one" keeps the conversation going; "the rest"
                    ends the questions for the turn. */}
                {qAllowDelegate && (
                  <div className="mcp-chat__wizard-delegate">
                    {/* "The rest" is FIRST in the DOM so it lands far left under
                        space-between, which also keeps tab order matching what the eye
                        sees. Red text marks the heavier action: it ends the questions
                        for the whole turn and cannot be undone, where skipping one is
                        per-question and reversible. */}
                    <button
                      data-testid="mcpChatQuestionDelegateRest"
                      className="mcp-chat__plan-btn mcp-chat__plan-btn--skip-all"
                      disabled={isActiveTabLoading}
                      onClick={() => onQuestionDelegateRest(msg.id, questionQuestionnaire)}
                    >
                      Skip the rest
                    </button>
                    <button
                      data-testid="mcpChatQuestionDelegateOne"
                      className="mcp-chat__plan-btn mcp-chat__plan-btn--reject"
                      disabled={isActiveTabLoading || qIsCurrentSkipped}
                      onClick={() => onQuestionDelegateOne(msg.id, qCurrentIndex, qCount)}
                    >
                      Skip this one
                    </button>
                  </div>
                )}
              </>
            );
          }
        }
        return <p className="mcp-chat__interrupt-desc">Clarifying questions</p>;
      }

      // Confirmation interrupt
      const confirmData = msg.interruptData as ConfirmationInterruptData;
      const { actionText, targetName, targetKuikaId, detailItems, priorFailure } = confirmData;
      const destructive: boolean = isDestructiveConfirmation(confirmData);
      return (
        <>
          {destructive && (
            <div className="mcp-chat__danger-header">
              <span className="mcp-chat__danger-icon" aria-hidden="true">
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 3.5l9 16H3z" />
                  <line x1="12" y1="10" x2="12" y2="14" />
                  <circle cx="12" cy="16.8" r="0.6" fill="currentColor" stroke="none" />
                </svg>
              </span>
              <span className="mcp-chat__danger-heading">
                <span className="mcp-chat__danger-title">Dangerous change</span>
                <span className="mcp-chat__danger-subtitle">This removes existing work — review before approving</span>
              </span>
            </div>
          )}
          {priorFailure && (
            <div className="mcp-chat__interrupt-prior-failure" role="alert">
              <p className="mcp-chat__interrupt-prior-failure-title">
                Previous attempt failed
                {priorFailure.attempts > 1 ? ` (${priorFailure.attempts} tries)` : ""}:
              </p>
              <p className="mcp-chat__interrupt-prior-failure-body">
                {priorFailure.errorText.length > 500
                  ? `${priorFailure.errorText.slice(0, 500)}…`
                  : priorFailure.errorText}
              </p>
            </div>
          )}
          <p className="mcp-chat__interrupt-desc">
            {actionText}
            {targetName && " "}
            {targetName && targetKuikaId ? (
              <span
                className="mcp-chat__interrupt-target"
                role="button"
                tabIndex={0}
                onClick={() => {
                  try {
                    const sd = (window as any).getSdInstance?.();
                    sd?.displayItemProvider?.setSelectedDisplayItem(targetKuikaId);
                  } catch {
                    /* ignore */
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    try {
                      const sd = (window as any).getSdInstance?.();
                      sd?.displayItemProvider?.setSelectedDisplayItem(targetKuikaId);
                    } catch {
                      /* ignore */
                    }
                  }
                }}
              >
                {targetName}
              </span>
            ) : (
              targetName && <span>{targetName}</span>
            )}
          </p>
          {detailItems && detailItems.length > 0 && (
            <div className="mcp-chat__migration-list">
              {detailItems.map((item, i) => (
                <div
                  key={i}
                  className={`mcp-chat__migration-card${item.isDrop ? " mcp-chat__migration-card--drop" : ""}`}
                >
                  <div className="mcp-chat__migration-card-header">
                    <svg
                      className="mcp-chat__migration-card-icon"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {item.isDrop ? (
                        <>
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </>
                      ) : (
                        <>
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <line x1="9" y1="3" x2="9" y2="21" />
                          <line x1="3" y1="9" x2="21" y2="9" />
                        </>
                      )}
                    </svg>
                    <span className="mcp-chat__migration-card-name">{item.label}</span>
                    {item.subtitle && <span className="mcp-chat__migration-card-count">{item.subtitle}</span>}
                  </div>
                  {item.columns && item.columns.length > 0 && (
                    <div className="mcp-chat__migration-card-cols">
                      {item.columns.map((col, ci) => (
                        <span key={ci} className="mcp-chat__migration-col">
                          {col}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      );
    }
    if (hasTaskPlan) {
      return (
        <>
          {msg.explorationContent && (
            <details className="mcp-chat__exploration-details">
              <summary>Context gathered during exploration</summary>
              <div className="mcp-chat__exploration-content">
                <MarkedReact value={msg.explorationContent} renderer={chatMarkdownRenderer} />
              </div>
            </details>
          )}
          {taskPlan!.textBefore && <MarkedReact value={taskPlan!.textBefore} renderer={chatMarkdownRenderer} />}
          <TaskPlanBlock plan={mergePlanCompletion(taskPlan!, planJson)} />
          {taskPlan!.textAfter && <MarkedReact value={taskPlan!.textAfter} renderer={chatMarkdownRenderer} />}
        </>
      );
    }
    if (hasQuestionnaire && currentQuestion) {
      if (normalizedProgress.submitted) {
        return renderAnsweredQuestionnaire(questionnaire!, normalizedProgress);
      }
      return (
        <>
          {questionnaire!.intro && <MarkedReact value={questionnaire!.intro} renderer={chatMarkdownRenderer} />}
          {questionCount > 1 && (
            <WizardStepper
              questions={questionnaire!.questions}
              progress={normalizedProgress}
              currentIndex={currentQuestionIndex}
              onGoTo={(stepIdx: number) => onQuestionGoTo(msg.id, stepIdx)}
              restrictBackOnly
            />
          )}
          {currentQuestion.prompt && (
            <div className="mcp-chat__question-prompt">
              <MarkedReact value={currentQuestion.prompt} renderer={chatMarkdownRenderer} />
            </div>
          )}
          {isCurrentMultiSelect && <div className="mcp-chat__multi-hint">Select all that apply</div>}
          <div
            data-testid="mcpChatQuestionnaire"
            className={`mcp-chat__options ${
              isCurrentMultiSelect ? "mcp-chat__options--multi" : "mcp-chat__options--single"
            }`}
          >
            {currentQuestion.options.map((opt, i) => {
              const isSelected = isCurrentMultiSelect
                ? multiValues.includes(opt.value)
                : selectedValue === opt.value ||
                  (isCustomSelected && isOtherSelection(currentQuestion ?? undefined, opt.value));
              return (
                <button
                  key={i}
                  data-testid={`mcpChatQuestionOption-${i}`}
                  className={`mcp-chat__option-chip ${isSelected ? "mcp-chat__option-chip--selected" : ""}`}
                  disabled={isActiveTabLoading}
                  onClick={() =>
                    isCurrentMultiSelect
                      ? onQuestionMultiToggle(msg.id, currentQuestionIndex, opt.value)
                      : onQuestionOptionSelect(
                          msg.id,
                          currentQuestionIndex,
                          opt.value,
                          questionCount,
                          currentQuestion ?? undefined
                        )
                  }
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {isCurrentMultiSelect && multiValues.length > 0 && (
            <div className="mcp-chat__multi-count">{multiValues.length} selected</div>
          )}
          {!isCurrentMultiSelect && isCustomSelected && (
            <input
              type="text"
              className="mcp-chat__custom-input"
              placeholder="Type custom value..."
              value={customValue}
              disabled={isActiveTabLoading}
              onChange={(e) => onQuestionCustomValueChange(msg.id, currentQuestionIndex, e.target.value)}
            />
          )}
          <div className="mcp-chat__wizard-nav">
            <button
              className="mcp-chat__wizard-btn"
              disabled={isActiveTabLoading || currentQuestionIndex === 0}
              onClick={() => onQuestionBack(msg.id)}
            >
              Back
            </button>
            {!isLastQuestion ? (
              <button
                className="mcp-chat__wizard-btn mcp-chat__wizard-btn--primary"
                disabled={isActiveTabLoading || !isCurrentAnswered}
                onClick={() => onQuestionNext(msg.id, questionCount, currentQuestion ?? undefined)}
              >
                Next
              </button>
            ) : (
              <button
                data-testid="mcpChatQuestionSubmit"
                className="mcp-chat__wizard-btn mcp-chat__wizard-btn--primary mcp-chat__wizard-btn--submit"
                disabled={isActiveTabLoading || !allQuestionsAnswered}
                onClick={() => onQuestionSubmit(msg.id, questionnaire!)}
              >
                Submit answers
              </button>
            )}
          </div>
          {questionnaire!.outro && <MarkedReact value={questionnaire!.outro} renderer={chatMarkdownRenderer} />}
        </>
      );
    }
    if (hasOptions) {
      return (
        <SimpleOptionsBlock parsed={parsed!} isActiveTabLoading={isActiveTabLoading} onOptionSelect={onOptionSelect} />
      );
    }
    const renderer = searchQuery ? buildSearchAwareRenderer(searchQuery, !!isActiveSearchMatch) : chatMarkdownRenderer;
    // Strip legacy `<!-- kuika-spec* -->` machine anchors if present (no longer emitted).
    const displayContent: string = stripSpecMachineAnchors(msg.content);
    return <MarkedReact value={displayContent} renderer={renderer} />;
  };

  return (
    <div
      className={`mcp-chat__message mcp-chat__message--${isUser ? "user" : "assistant"}`}
      data-testid={isUser ? "mcpChatUserMessage" : "mcpChatAssistantMessage"}
      style={staggerIndex != null ? ({ "--msg-stagger": staggerIndex } as React.CSSProperties) : undefined}
    >
      {!isUser && (
        <div className="mcp-chat__avatar">
          <KuikaSvg name="sparkles" type="datasource-icons" width={14} height={14} />
        </div>
      )}
      {!isUser && msg.completedToolActivities && msg.completedToolActivities.length > 0 && (
        <McpToolTranscript
          activities={msg.completedToolActivities}
          searchQuery={searchQuery}
          isActiveSearchMatch={isActiveSearchMatch}
          chatActive={isActiveTabLoading}
        />
      )}
      {shouldRenderBubbleRow && (
        <div className={`mcp-chat__bubble-row${!isUser ? " mcp-chat__bubble-row--assistant" : ""}`}>
          {isUser && rewindEnabled && msg.stagedOpIdsBeforeSend !== undefined && (
            <button
              className="mcp-chat__rewind-hover-btn"
              onClick={() => onUndo(msg.id)}
              disabled={isActiveTabLoading}
              title={isMostRecentUndoable ? "Revert last message" : "Revert to this message"}
              aria-label="Revert to this message"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="1 4 1 10 7 10" />
                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
              </svg>
            </button>
          )}
          <div className={bubbleClass} data-testid={bubbleTestId}>
            {isUser && msg.images && msg.images.length > 0 && (
              <div className="mcp-chat__msg-images">
                {msg.images.map((img, i) => (
                  <img key={i} src={img.previewUrl} alt="attached" className="mcp-chat__msg-image" />
                ))}
              </div>
            )}
            {isUser && msg.hadImages && !msg.images && (
              <span className="mcp-chat__msg-images-placeholder">Image(s) from previous session</span>
            )}
            {isUser && msg.files && msg.files.length > 0 && (
              <div className="mcp-chat__msg-file-badges">
                {msg.files.map((f, i) => {
                  const info = getFileInfo(f.fileName);
                  const sizeKb = Math.round(f.fileSize / 1024);
                  const sizeLabel = sizeKb >= 1024 ? `${(sizeKb / 1024).toFixed(1)} MB` : `${sizeKb} KB`;
                  return (
                    <div key={i} className="mcp-chat__msg-file-badge">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={info.color}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                      <span className="mcp-chat__msg-file-badge-name">{formatFileNameForDisplay(f.fileName)}</span>
                      <span className="mcp-chat__msg-file-badge-size">{sizeLabel}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {isUser && msg.hadFiles && !msg.files && (
              <span className="mcp-chat__msg-files-placeholder">File(s) from previous session</span>
            )}
            {isUser && msg.selectedElementSegments && msg.selectedElementSegments.length > 0 ? (
              <div className="mcp-chat__selected-element-segments">
                {msg.selectedElementSegments.map((seg, idx) => (
                  <div key={`${seg.attachment.kuikaId}-${idx}`} className="mcp-chat__selected-element-segment">
                    <SelectedElementBadge attachment={seg.attachment} muted />
                    {seg.promptText && <div className="mcp-chat__selected-element-segment-text">{seg.promptText}</div>}
                  </div>
                ))}
                {msg.content && (
                  <div className="mcp-chat__selected-element-segment-text mcp-chat__selected-element-segment-text--trailing">
                    {msg.content}
                  </div>
                )}
              </div>
            ) : (
              <>
                {isUser && msg.selectedElementAttachments && msg.selectedElementAttachments.length > 0 && (
                  <div className="mcp-chat__selected-element-badges">
                    {msg.selectedElementAttachments.map((att) => (
                      <SelectedElementBadge key={att.kuikaId} attachment={att} muted />
                    ))}
                  </div>
                )}
                {renderBubbleContent()}
              </>
            )}
          </div>
        </div>
      )}
      {!isUser && !msg.isError && !msg.isStreaming && (
        <McpChatMessageFooter
          chatRequestId={msg.chatRequestId}
          content={msg.content}
          userRating={msg.userRating}
          isLast={isLastAssistant}
          onRate={onRate}
        />
      )}
      {!isUser && !msg.isError && tabPlanMode && detectPlanMessage(msg.content) && !msg.planStatus && (
        <div className="mcp-chat__plan-actions">
          <button
            className="mcp-chat__plan-btn mcp-chat__plan-btn--approve"
            onClick={() => onPlanApprove(msg.id)}
            disabled={isActiveTabLoading}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Execute Plan
          </button>
          <button
            className="mcp-chat__plan-btn mcp-chat__plan-btn--reject"
            onClick={() => onPlanReject(msg.id)}
            disabled={isActiveTabLoading}
          >
            Cancel
          </button>
        </div>
      )}
      {msg.planStatus === "approved" && (
        <div className="mcp-chat__plan-status mcp-chat__plan-status--approved">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Plan approved and executing
        </div>
      )}
      {msg.planStatus === "rejected" && (
        <div className="mcp-chat__plan-status mcp-chat__plan-status--rejected">Plan cancelled</div>
      )}
      {/* Business Analyst spec approval (KOA-17628) — reuses the plan action/status styling. */}
      {!isUser && !msg.isError && detectSpecMessage(msg.content) && !msg.specStatus && (
        <div className="mcp-chat__plan-actions">
          <button
            className="mcp-chat__plan-btn mcp-chat__plan-btn--approve"
            data-testid="mcpChatSpecBuildButton"
            onClick={() => onSpecApprove(msg.id)}
            disabled={isActiveTabLoading}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Build
          </button>
          <button
            className="mcp-chat__plan-btn mcp-chat__plan-btn--reject"
            data-testid="mcpChatSpecReviseButton"
            onClick={() => onSpecReject(msg.id)}
            disabled={isActiveTabLoading}
          >
            Revise
          </button>
        </div>
      )}
      {/* KOA-18422 — change-plan proposal (Approve / Revise). The anchor carries the
          delta id; approving applies WHAT WAS SHOWN rather than re-driving the turn
          with this message's markdown, which is what the BA spec card above does. */}
      {!isUser && !msg.isError && !msg.changeStatus && parseChangeDeltaId(msg.content) !== null && (
        <div className="mcp-chat__plan-actions">
          <button
            className="mcp-chat__plan-btn mcp-chat__plan-btn--approve"
            data-testid="mcpChatChangeApproveButton"
            onClick={() => onChangeApprove(msg.id, parseChangeDeltaId(msg.content) ?? "")}
            disabled={isActiveTabLoading}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Approve
          </button>
          <button
            className="mcp-chat__plan-btn mcp-chat__plan-btn--reject"
            data-testid="mcpChatChangeReviseButton"
            onClick={() => onChangeReject(msg.id, parseChangeDeltaId(msg.content) ?? "")}
            disabled={isActiveTabLoading}
          >
            Revise
          </button>
        </div>
      )}
      {msg.changeStatus === "approved" && (
        <div className="mcp-chat__plan-status mcp-chat__plan-status--approved">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Change approved — applying
        </div>
      )}
      {msg.changeStatus === "rejected" && (
        <div className="mcp-chat__plan-status mcp-chat__plan-status--rejected">Change plan discarded</div>
      )}
      {msg.specStatus === "approved" && (
        <div className="mcp-chat__plan-status mcp-chat__plan-status--approved">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Spec approved — building
        </div>
      )}
      {msg.specStatus === "rejected" && (
        <div className="mcp-chat__plan-status mcp-chat__plan-status--rejected">
          Revise the spec — send your changes below
        </div>
      )}
      {msg.interruptData && msg.interruptData.type === "confirmation" && msg.interruptData.status === "pending" && (
        <InterruptActions
          messageId={msg.id}
          isActiveTabLoading={isActiveTabLoading}
          onApprove={onInterruptApprove}
          onApproveAll={onInterruptApproveAll}
          onReject={onInterruptReject}
        />
      )}
      {msg.interruptData?.status === "approved" && (
        <div className="mcp-chat__plan-status mcp-chat__plan-status--approved">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Approved
        </div>
      )}
      {msg.interruptData?.status === "rejected" && (
        <div className="mcp-chat__plan-status mcp-chat__plan-status--rejected">Cancelled</div>
      )}
      {msg.interruptData?.status === "expired" && (
        <div className="mcp-chat__plan-status mcp-chat__plan-status--expired">Session expired</div>
      )}
      {msg.isError && (
        <button
          className="mcp-chat__retry-btn"
          onClick={() => onRetry(msg.id)}
          disabled={isActiveTabLoading}
          aria-label="Retry message"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
          Retry
        </button>
      )}
      {!isUser && rewindEnabled && undoButtonTargetUserMsgId && (
        <button
          className="mcp-chat__undo-btn"
          onClick={() => onUndo(undoButtonTargetUserMsgId)}
          disabled={isActiveTabLoading}
          aria-label="Revert last AI changes"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="1 4 1 10 7 10" />
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
          </svg>
          Revert
        </button>
      )}
      {!isUser && msg.structuralDiff && msg.structuralDiff.totalChanges > 0 && (
        <StructuralDiffBlock diff={msg.structuralDiff} />
      )}
      <span className="mcp-chat__timestamp">
        {formatTime(msg.timestamp)}
        {!isUser && msg.responseDurationMs != null && (
          <span className="mcp-chat__duration"> · {formatDuration(msg.responseDurationMs)}</span>
        )}
      </span>
    </div>
  );
};

export default memo(McpChatMessageComponent, (prev, next) => {
  if (prev.message !== next.message) return false;
  if (prev.isActiveTabLoading !== next.isActiveTabLoading) return false;
  if (prev.isMostRecentUndoable !== next.isMostRecentUndoable) return false;
  if (prev.rewindEnabled !== next.rewindEnabled) return false;
  if (prev.undoButtonTargetUserMsgId !== next.undoButtonTargetUserMsgId) return false;
  if (prev.questionnaireProgress !== next.questionnaireProgress) return false;
  if (prev.tabPlanMode !== next.tabPlanMode) return false;
  if (prev.searchQuery !== next.searchQuery) return false;
  if (prev.isActiveSearchMatch !== next.isActiveSearchMatch) return false;
  // Only re-render for planJson changes if this message contains a plan
  if (prev.planJson !== next.planJson) {
    const content = next.message.content;
    if (detectPlanMessage(content) || /<task_plan>/.test(content)) return false;
  }
  return true;
});
